import React, { useEffect, useRef } from 'react';
import { 
  GlobeHemisphereWest, 
  Translate, 
  ChatCircleText,
  BookOpenText,
  SpeakerHigh,
  MusicNotes,
  Books,
  Scroll,
  Star,
  UsersThree,
  Crown,
  Megaphone,
  Storefront,
  TrendUp,
  CurrencyDollar,
  Brain,
  MoonStars,
  Sparkle
} from '@phosphor-icons/react';
import './ProgramSection.css';

const programs = [
  {
    number: '01',
    title: 'Bahasa Asing',
    description: 'Penguasaan Bahasa Arab dan Inggris aktif melalui lingkungan bilingual, praktik harian, dan program pertukaran.',
    icons: {
      primary: GlobeHemisphereWest,
      secondary: Translate,
      accent: ChatCircleText,
    },
    iconColors: {
      primary: 'text-forest',
      secondary: 'text-forest',
      accent: 'text-forest-50',
    }
  },
  {
    number: '02',
    title: 'PTQIC',
    description: 'Pendidikan Tilawatil Qur\'an dengan metode sistematis, fokus tajwid, makhraj, dan sifat huruf.',
    icons: {
      primary: BookOpenText,
      secondary: SpeakerHigh,
      accent: MusicNotes,
    },
    iconColors: {
      primary: 'text-forest',
      secondary: 'text-amber-500',
      accent: 'text-forest-30',
    }
  },
  {
    number: '03',
    title: 'Kitab Kuning',
    description: 'Kajian mendalam fiqih, ushul fiqh, hadits, tafsir dengan bimbingan kyai berpengalaman.',
    icons: {
      primary: Books,
      secondary: Scroll,
      accent: Star,
    },
    iconColors: {
      primary: 'text-amber-600',
      secondary: 'text-amber-700',
      accent: 'text-forest-40',
    }
  },
  {
    number: '04',
    title: 'Kepemimpinan',
    description: 'Pembentukan jiwa pemimpin melalui organisasi santri, manajemen, dan public speaking.',
    icons: {
      primary: UsersThree,
      secondary: Crown,
      accent: Megaphone,
    },
    iconColors: {
      primary: 'text-forest',
      secondary: 'text-amber-500',
      accent: 'text-forest-30',
    }
  },
  {
    number: '05',
    title: 'Kewirausahaan',
    description: 'Pelatihan bisnis, manajemen keuangan, dan praktik langsung di unit usaha pesantren.',
    icons: {
      primary: Storefront,
      secondary: TrendUp,
      accent: CurrencyDollar,
    },
    iconColors: {
      primary: 'text-forest',
      secondary: 'text-forest',
      accent: 'text-amber-500',
    }
  },
  {
    number: '06',
    title: 'Tahfidz Takhosus',
    description: 'Program hafalan Al-Qur\'an sistematis dengan bimbingan hafidz berpengalaman.',
    icons: {
      primary: Brain,
      secondary: MoonStars,
      accent: Sparkle,
    },
    iconColors: {
      primary: 'text-forest',
      secondary: 'text-amber-500',
      accent: 'text-forest-40',
    }
  },
];

const ProgramCard = ({ program, index }) => {
  const cardRef = useRef(null);
  const { primary: PrimaryIcon, secondary: SecondaryIcon, accent: AccentIcon } = program.icons;

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 15;
      const rotateY = (centerX - x) / 15;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-12px) scale(1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div 
      className="stagger-enter" 
      style={{ animationDelay: `${0.2 + index * 0.1}s` }}
    >
      <div 
        ref={cardRef}
        className="program-card"
      >
        <div className="icon-bg-shape"></div>
        
        {/* Icon Cluster */}
        <div className="icon-cluster">
          <div className="icon-cluster-inner">
            {/* Primary Icon */}
            <div className="combo-icon combo-icon-primary">
              <PrimaryIcon weight="duotone" className={`icon-primary ${program.iconColors.primary}`} />
            </div>
            
            {/* Secondary Icon */}
            <div className="combo-icon combo-icon-secondary">
              <SecondaryIcon weight="duotone" className={`icon-secondary ${program.iconColors.secondary}`} />
            </div>
            
            {/* Accent Icon */}
            <div className="combo-icon combo-icon-accent">
              <AccentIcon weight="fill" className={`icon-accent ${program.iconColors.accent}`} />
            </div>
          </div>
        </div>

        <div className="program-card-content">
          <div className="number-pill">{program.number}</div>
          <h3 className="program-card-title">{program.title}</h3>
          <p className="program-card-desc">{program.description}</p>
        </div>
      </div>
    </div>
  );
};

const ProgramSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.stagger-enter');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="programs" className="program-section" ref={sectionRef}>
      {/* Decorative Background */}
      <div className="program-decorative-bg">
        <div className="floating-blob blob-1"></div>
        <div className="floating-blob blob-2"></div>
      </div>

      <div className="container-tight program-container">
        {/* Header */}
        <div className="program-header stagger-enter is-visible" style={{ animationDelay: '0.1s' }}>
          <div className="program-header-grid">
            <div className="program-header-left">
              <span className="program-label">PROGRAM UNGGULAN</span>
              <h2 className="program-title">
                Mengapa <br/>
                <span className="program-title-muted">Bahrul Ulum?</span>
              </h2>
            </div>
            <div className="program-header-right">
              <p className="program-description">
                Program unggulan yang membedakan kami dan memastikan 
                setiap santri mendapatkan pendidikan terbaik.
              </p>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="program-grid">
          {programs.map((program, index) => (
            <ProgramCard key={program.number} program={program} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProgramSection;
